import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Box, Button, TextField } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers';
import DropdownMenu from 'src/components/DropdownMenu';
import { DaysForFirstRentAfterContractStartList } from 'src/utils/constants/distributors';
import { calculateFirstRentDateFromGracePeriod } from 'src/utils/contracts';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import { selectDistributorDetail } from 'src/store/selectors/distributorSelectors';
import { useSelector } from 'react-redux';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const StepOne = ({ data, handleNext, resetRef }) => {
  const { classes } = useStyles();
  const parameter = useSelector(selectDistributorDetail);
  const initialState = useMemo(() => makeInitialState(data, parameter), [data]);

  const { t } = useTranslation();

  const onNext = useCallback(
    values => {
      handleNext(values);
    },
    [handleNext]
  );

  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onNext}
    >
      {({
        errors,
        handleSubmit,
        touched,
        values,
        setFieldValue,
        handleBlur,
        handleChange,
        resetForm
      }) => {
        resetRef.current = () => resetForm();
        return (
          <form onSubmit={handleSubmit}>
            <Box className={classes.container}>
              <DatePicker
                label={t('COMMON.START_DATE')}
                value={values.startDate}
                format="DD/MM/YYYY"
                disabled
                onChange={newValue => {
                  setFieldValue('startDate', momentLocalTimezone(newValue));
                  setFieldValue(
                    'firstRentDate',
                    calculateFirstRentDateFromGracePeriod(
                      momentLocalTimezone(newValue),
                      values.gracePeriod
                    )
                  );
                  setFieldValue(
                    'endDate',
                    calculateFirstRentDateFromGracePeriod(
                      momentLocalTimezone(newValue),
                      values.gracePeriod
                    ).add(data.term - 1, 'months')
                  );
                }}
                slotProps={{
                  textField: {
                    sx: { width: '100%', margin: '16px 0' },
                    error: Boolean(touched.startDate && errors.startDate),
                    helperText: touched.startDate && t(errors.startDate),
                    FormHelperTextProps: { sx: { color: 'red' } },
                    InputProps: { autoComplete: 'off' }
                  }
                }}
              />

              <DropdownMenu
                list={DaysForFirstRentAfterContractStartList}
                label={t('COMMON.DAYS_FOR_FIRST_RENT')}
                value={values.gracePeriod}
                disabled={data.gracePeriod != null}
                name="gracePeriod"
                setValue={value => {
                  setFieldValue('gracePeriod', value);
                  setFieldValue(
                    'firstRentDate',
                    calculateFirstRentDateFromGracePeriod(
                      momentLocalTimezone(values.startDate),
                      value
                    )
                  );
                  setFieldValue(
                    'endDate',
                    calculateFirstRentDateFromGracePeriod(
                      momentLocalTimezone(values.startDate),
                      value
                    ).add(data.term - 1, 'months')
                  );
                }}
                error={Boolean(touched.gracePeriod && errors.gracePeriod)}
                errorText={touched.gracePeriod && t(errors.gracePeriod)}
              />

              {!values.isFirstUpfrontRentModality && (
                <DatePicker
                  label={t('CONTRACT_VIEW.FIRST_RENT_DATE')}
                  value={values.firstRentDate}
                  format="DD/MM/YYYY"
                  disabled
                  onChange={newValue => {
                    setFieldValue(
                      'firstRentDate',
                      momentLocalTimezone(newValue)
                    );
                  }}
                  slotProps={{
                    textField: {
                      sx: { width: '100%', margin: '16px 0' },
                      error: Boolean(touched.firstRentDate && errors.firstRentDate),
                      helperText: touched.firstRentDate && t(errors.firstRentDate),
                      FormHelperTextProps: { sx: { color: 'red' } },
                      InputProps: { autoComplete: 'off' }
                    }
                  }}
                />
              )}

              {values.isFirstUpfrontRentModality && (
                <DatePicker
                  label={t('CONTRACT_VIEW.SECOND_RENT_DATE')}
                  value={values.secondRentDate}
                  format="DD/MM/YYYY"
                  minDate={momentLocalTimezone(values.startDate).add(
                    2,
                    'months'
                  )}
                  onChange={newValue => {
                    setFieldValue(
                      'secondRentDate',
                      momentLocalTimezone(newValue)
                    );
                    setFieldValue(
                      'endDate',
                      momentLocalTimezone(newValue).add(data.term - 2, 'months')
                    );
                  }}
                  disabled
                  slotProps={{
                    textField: {
                      sx: { width: '100%', margin: '16px 0' },
                      error: Boolean(touched.secondRentDate && errors.secondRentDate),
                      helperText: touched.secondRentDate && t(errors.secondRentDate),
                      FormHelperTextProps: { sx: { color: 'red' } },
                      InputProps: { autoComplete: 'off' }
                    }
                  }}
                />
              )}

              <DatePicker
                label={t('COMMON.END_DATE')}
                value={values.endDate}
                format="DD/MM/YYYY"
                disabled
                onChange={() => {}}
                slotProps={{
                  textField: {
                    sx: { width: '100%', margin: '16px 0' },
                    error: Boolean(touched.endDate && errors.endDate),
                    helperText: touched.endDate && t(errors.endDate),
                    FormHelperTextProps: { sx: { color: 'red' } },
                    InputProps: { autoComplete: 'off' }
                  }
                }}
              />
              <TextField
                inputProps={{
                  min: 1,
                  max: 25,
                  autoComplete: 'off'
                }}
                error={Boolean(touched.payday && errors.payday)}
                fullWidth
                helperText={touched.payday && t(errors.payday)}
                label={t('NAVIGATION.PAY_DAY')}
                margin="normal"
                name="payday"
                onBlur={handleBlur}
                onChange={handleChange}
                type="number"
                value={values.payday}
                variant="outlined"
              />
            </Box>
            <Box
              className={classes.box}
              display="flex"
              justifyContent="flex-end"
            >
              <Button
                className={clsx(classes.buttons, classes.buttonMain)}
                type="submit"
                color="primary"
                variant="outlined"
              >
                {t('COMMON.NEXT')}
              </Button>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};
StepOne.propTypes = {
  data: PropTypes.object,
  resetRef: PropTypes.object,
  handleNext: PropTypes.func
};

export default StepOne;
